
/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

// helper that determines user entitlement based on user plan code

export const USERTYPE = {
	FREE: 'Free',
	CCPP: 'CCPP',
	ET_ALL_APPS: 'ETAllApps',
	ALL_APPS: 'AllApps',
	SINGLE_APP: 'SingleApp',
	OTHER: 'Other',
	MULTI: 'Multi',
	CCE_FREE: 'CCE_Free',
	COMM: 'Communicator'
};

import FeatureFlagsUtils from '../../utils/FeatureFlagsUtils';

class UserTypeUtil {
	
	static isFree(planCode: string): boolean {
		switch (planCode) {
			case 'cc_free':
			case 'dc_free':
				return true;
			default:
				return false;
		}
	}

	static isCCPP(planCode: string): boolean {
		// Only get CCPP entitlements that includes LR and PS
		switch (planCode) {
			case 'phlt_direct_individual':
			case 'ples_1tb_direct_individual':
			case 'ples_2tb_direct_individual':
			case 'ples_5tb_direct_individual':
			case 'ples_10tb_direct_individual':
			case 'ppsk_direct_individual':
				return true;
			default:
				return false;
		}
	}

	static isETAllApps(planCode: string): boolean {
		switch (planCode) {
			case 'ccle_direct_indirect_team':
			case 'ccsv_enterprise_ml':
			case 'ccle_indirect_enterprise_ml_edu_email_k12':
			case 'ccle_indirect_enterprise_ml':
			case 'ctsk_direct_indirect_team':
			case 'ccsv_k12_enterprise_ml_edu_email_k12':
			case 'ccle_indirect_team_chn':
			case 'creative_cloud_all_apps_student_license_enterprise':
			case 'ccle_indirect_team_device':
			case 'ccsv_enterprise_chn':
			case 'cce_all_apps_multi_user_enterprise':
			case 'ccle_indirect_enterprise_ml_edu_email_hed':
			case 'cce_all_apps_esm_for_china_enterprise':
			case 'cce_all_apps_for_hed_single_frl_enterprise_frl':
			case 'cce_all_apps_for_single_frl_for_gov_in_vip_enterprise_frl':
			case 'cce_all_apps_multi_user_esm_enterprise':
			case 'cce_all_aps_esm_enterprise':
			case 'cce_pro_all_apps_enterprise':
			case 'ccle_indirect_enterprise_nuell_edu_hed_ml':
			case 'ccle_indirect_enterprise_nuell_edu_k12_ml':
			case 'ccsv_enterprise_frl_multi_ml':
			case 'ccsv_enterprise_frl_single_ml':
			case 'ccsv_enterprise_ms_ml':
			case 'ccsv_enterprise_nuell_hed_ml':
			case 'ccsv_enterprise_nuell_k12_ml':
			case 'creative_cloud_all_apps_for_team_hed_vip_team':	
				return true;
		}
		if (planCode.indexOf('enterprise') >= 0 || planCode.indexOf('team') >= 0) {
			return true;
		}

		return false;
	}

	static isAllApps(planCode: string): boolean {
		switch (planCode) {
			// Individual All Apps Entitlements
			case 'ccsn_direct_individual':
			case 'cisk_direct_individual':
			case 'creative_cloud_all_apps_with_1tb_individual':
			case 'creative_cloud_all_apps_with_2tb_cloud_services_individual':
			case 'creative_cloud_all_apps_with_5tb_cloud_services_individual':
			case 'creative_cloud_all_apps_with_10_tb_cloud_services_individual':
				return true;
		}
		
		return false;
	}

	static isSingleApp(planCode: string): boolean {
		switch (planCode) {
			case 'phsp_direct_individual':
			case 'ilst_direct_individual':
			case 'idsn_direct_individual':
			case 'sprk_direct_individual':
			case 'apcc_direct_individual':
			case 'drwv_direct_individual':
			case 'flpr_direct_individual':
			case 'ppro_direct_individual':
			case 'apap_direct_individual':
			case 'apro_direct_individual':
			case 'acro_direct_individual':
			case 'aeft_direct_individual':
			case 'aspk_direct_individual':
			case 'eshr_direct_individual':
			case 'audt_direct_individual':
			case 'aicy_direct_individual':
			case 'adobe_premiere_rush_cc_with_100gb_individual':
			case 'adobe_premiere_rush_cc_with_1tb_individual':
			case 'adobe_premiere_rush_cc_with_2tb_individual':
			case 'adobe_premiere_rush_cc_with_5tb_individual':
			case 'adobe_premiere_rush_cc_with_10tb_individual':
			case 'lpes_1tb_direct_individual':
			case 'lpes_2tb_direct_individual':
			case 'lpes_5tb_direct_individual':
			case 'lpes_10tb_direct_individual':
			case 'premiere_pro_cc_single_app_with_1tb_individual':
			case 'premiere_pro_cc_single_app_with_2tb_individual':
			case 'premiere_pro_cc_single_app_with_5tb_individual':
			case 'premiere_pro_cc_single_app_with_10tb_individual':
				return true;
		}
		return false;
	}

	static isCCEFree(planCode) {
		if (!FeatureFlagsUtils.hasFeature('home_new_plan_ui')) {
			return false;
		}
		switch (planCode) {
			case 'cce_free_etla_enterprise':
			case 'cce_free_evip_enterprise':
				return true;
		}
		return false;
	}

	static isComm(planCode) {
		if (!FeatureFlagsUtils.hasFeature('home_new_plan_ui')) {
			return false;
		}
		switch (planCode) {
			case 'adobe_creative_cloud_communicator_etla_enterprise':
				return true;
		}
		return false;
	}

	static isValidV3UserType() {
		if (UserTypeUtil.getUserType() === USERTYPE.ALL_APPS
		|| UserTypeUtil.getUserType() === USERTYPE.SINGLE_APP
		|| UserTypeUtil.getUserType() === USERTYPE.OTHER
		|| UserTypeUtil.getUserType() === USERTYPE.CCPP
		|| UserTypeUtil.getUserType() === USERTYPE.MULTI) {
			return true;
		}
		return false;
	}

	static isETUserType(paid) {
		for (let sub of paid) {
			if (UserTypeUtil.isETAllApps(sub.planCode)) {
				return true;
			}
		}
		return false;
	}

	static getUserType() {
		if (sessionStorage && sessionStorage.getItem('cchome_raw_profile')) {
			const profile = JSON.parse(sessionStorage.getItem('cchome_raw_profile'));
			const subs = profile.account.subscriptions;
			const {free, paid} = subs;
			
			if (free.length && !paid.length) {
				return USERTYPE.FREE;
			} 
			const planCode = paid[0].planCode;

			if (UserTypeUtil.isCCEFree(planCode)) {
				return USERTYPE.CCE_FREE;
			}
			else if (UserTypeUtil.isComm(planCode)) {
				return USERTYPE.COMM;
			}
			else if (UserTypeUtil.isETUserType(paid)) {
				return USERTYPE.ET_ALL_APPS;
			}

			if (paid.length > 1) {
				// multi plan users are generic as well
				return USERTYPE.MULTI;
			} 
			
			if (UserTypeUtil.isCCPP(planCode)) {
				return USERTYPE.CCPP;
			} 
			else if (UserTypeUtil.isAllApps(planCode)) {
				return USERTYPE.ALL_APPS;
			}
			else if (UserTypeUtil.isSingleApp(planCode)) {
				return USERTYPE.SINGLE_APP;
			}
			return USERTYPE.OTHER;

		}
	}
}

export default UserTypeUtil;